import React from 'react';
import { FieldMetaState } from 'react-final-form';

import { PricingFormsPayloadForConfirmation } from '../../api/api';
import { PricingFormCartItem } from '../../state/types';
import { Nullable, PricingFormFieldType, PricingFormOptionType, PricingFormType } from '../../types/types';
import { mapKeysToSnakeCaseDeep } from '../../utils/snakeCaseKeys';

// Pretend you don't see this
export const NO_UPPER_BOUND_VALUE_BUT_IN_ASCII = 2147483647; // don't check
export const isNoUpperBound = (option: PricingFormOptionType) =>
  option.upperBound === NO_UPPER_BOUND_VALUE_BUT_IN_ASCII;

// NOTE: These helper functions are copy-pasted from `components/BillPay/Shared/Utils/FormUtils`
export const getErrorMessage = (
  { touched, error = [] }: FieldMetaState<unknown>,
  helperText?: React.ReactNode | null
) => {
  const [errorMessage] = error;

  return (touched && errorMessage) || helperText;
};

export const hasError = ({ touched, error }: FieldMetaState<unknown>) => {
  return Boolean(touched && error);
};

export type FormValues = { [key: string]: number | number[] | null };

export const getFieldValueKey = (field: PricingFormFieldType) => {
  return `id-${field.id}`;
};

export const generatePricingFormInitialValues = (config: PricingFormType) => {
  const result: FormValues = {};
  config.fields.forEach((field: PricingFormFieldType) => {
    switch (field.kind) {
      case 'quantity_select':
        result[getFieldValueKey(field)] = 0;
        break;
      case 'multiple_select':
        result[getFieldValueKey(field)] = [];
        break;
      case 'single_select':
      case 'numerical_range':
        result[getFieldValueKey(field)] = field.options ? field.options[0]?.id || null : null;
        break;
      default:
        break;
    }
  });
  return result;
};

export const calculatePricingFormTotalPrice = (config: PricingFormType, values: FormValues): number => {
  return config.fields.reduce((currentValue: number, field: PricingFormFieldType) => {
    switch (field.kind) {
      case 'quantity_select': {
        const quantity = values[getFieldValueKey(field)] as number;
        return currentValue + (field.price || 0) * quantity;
      }
      case 'numerical_range':
      case 'single_select': {
        const singleSelectValue = values[getFieldValueKey(field)] as number;
        const selectedOption = field.options?.find((x) => x.id === singleSelectValue);
        return currentValue + (selectedOption?.price || 0);
      }
      case 'multiple_select': {
        const value = values[getFieldValueKey(field)] as number[];
        const selectedOptions = field.options?.filter((x) => value?.includes(x.id));
        return (
          currentValue +
          (selectedOptions?.reduce((current: number, option: PricingFormOptionType) => {
            return current + (option.price || 0);
          }, 0) || 0)
        );
      }
      default: {
        return currentValue;
      }
    }
  }, 0);
};

export const generateDescription = (config: PricingFormType, values: FormValues): string => {
  const result = config.fields.map((field: PricingFormFieldType) => {
    switch (field.kind) {
      case 'quantity_select': {
        const quantity = values[getFieldValueKey(field)] as number;
        return `${quantity} ${field.name}`;
      }
      case 'single_select': {
        const singleValue = values[getFieldValueKey(field)] as number;
        const singleSelectOption = field.options?.find((option) => option.id === singleValue);
        return `${field.name}: ${singleSelectOption?.name || ''}`;
      }
      case 'numerical_range': {
        const numericalValue = values[getFieldValueKey(field)] as number;
        const numericalOptionSelected = field.options?.find((option) => option.id === numericalValue);
        const noUpperBound = numericalOptionSelected ? isNoUpperBound(numericalOptionSelected) : false;
        return noUpperBound
          ? `More than ${numericalOptionSelected?.lowerBound}`
          : `${numericalOptionSelected?.lowerBound} - ${numericalOptionSelected?.upperBound}`;
      }
      case 'multiple_select': {
        const multiValue = values[getFieldValueKey(field)] as number[];
        const selectedOptions = field.options?.filter((option) => multiValue.includes(option.id));
        return selectedOptions
          ?.reduce((currentValue: string[], option: PricingFormOptionType) => {
            return [...currentValue, option.name];
          }, [])
          .join(', ');
      }
      default: {
        return null;
      }
    }
  });
  return result.filter(Boolean).join(', ');
};

const getSelectedFromValue = (item: PricingFormCartItem, field: PricingFormFieldType): Nullable<number[]> => {
  if (field.kind === 'quantity_select') {
    return null;
  }

  const value = item.values[getFieldValueKey(field)];
  if (typeof value === 'number') {
    return [value];
  }
  if (Array.isArray(value)) {
    return value as number[];
  }
  return null;
};

const getValue = (item: PricingFormCartItem, field: PricingFormFieldType): Nullable<number> => {
  if (field.kind !== 'quantity_select') {
    return null;
  }
  const value = item.values[getFieldValueKey(field)];
  if (typeof value === 'number') {
    return value;
  }
  return null;
};

export const mapPriceFormCartItemsToConfirmPayload = (cartItem: PricingFormCartItem) =>
  mapKeysToSnakeCaseDeep({
    uuid: cartItem.pricingForm.uuid,
    name: cartItem.pricingForm.name,
    description: cartItem.pricingForm?.description,
    fields: cartItem.pricingForm.fields.map((field) => ({
      ...field,
      selected: getSelectedFromValue(cartItem, field),
      value: getValue(cartItem, field),
    })),
  }) as PricingFormsPayloadForConfirmation;
